import { CheckCircle2, XCircle, Gauge, MapPin, FileSignature, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { CategoryBadge, StatusBadge } from "@/components/Badges";
import { fmtDateTime, MGPS_CATS } from "@/lib/api";

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-sm text-slate-800 mt-0.5">{children}</div>
  </div>
);

export const TaskVerifyDialog = ({ task, onOpenChange }) => {
  if (!task) return null;
  const mgps = MGPS_CATS.includes(task.category);
  return (
    <Dialog open={!!task} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="director-verify-task-modal">
        <DialogHeader>
          <div className="flex items-center gap-2"><CategoryBadge category={task.category} /><StatusBadge status={task.status} /></div>
          <DialogTitle className="font-heading text-2xl pt-1">{task.task_title}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-3 gap-4 p-4 rounded-xl bg-slate-50 border">
          <Field label="Hospital">{task.client?.hospital_name}</Field>
          <Field label="Engineer">{task.assignee_name}</Field>
          <Field label="Completed">{fmtDateTime(task.completion_time)}</Field>
          <Field label="Started">{fmtDateTime(task.start_time)}</Field>
          <Field label="Start GPS">{task.start_lat != null ? <span className="font-mono-num text-xs">{task.start_lat.toFixed(4)}, {task.start_lng.toFixed(4)}</span> : "—"}</Field>
          <Field label="Completion GPS">{task.completion_lat != null ? <span className="font-mono-num text-xs">{task.completion_lat.toFixed(4)}, {task.completion_lng.toFixed(4)}</span> : "—"}</Field>
        </div>
        {mgps && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border p-4 flex items-center gap-3" data-testid="verify-psi-reading">
              <Gauge className="h-8 w-8 text-sky-600" />
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">O₂ Line Pressure</div>
                <div className="font-mono-num text-2xl font-semibold text-slate-900">{task.mgps_oxygen_pressure_psi ?? "—"} <span className="text-sm text-slate-500">PSI</span></div></div>
            </div>
            <div className="rounded-xl border p-4 flex items-center gap-3" data-testid="verify-leakage-result">
              {task.mgps_leakage_test_passed ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : <XCircle className="h-8 w-8 text-rose-600" />}
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Leakage Test</div>
                <div className="font-heading text-xl font-bold">{task.mgps_leakage_test_passed == null ? "—" : task.mgps_leakage_test_passed ? "Passed" : "Failed"}</div></div>
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2"><Camera className="h-3.5 w-3.5" />Site Photo</div>
            <AuthImage path={task.proof_photo_url} alt="proof" testid="verify-proof-photo" className="w-full h-56 object-cover rounded-xl border" />
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2"><FileSignature className="h-3.5 w-3.5" />Hospital Signature</div>
            <AuthImage path={task.customer_signature_url} alt="signature" testid="verify-signature" className="w-full h-56 object-contain rounded-xl border bg-white" />
          </div>
        </div>
        {task.checklist?.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Checklist</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {task.checklist.map((c) => (
                <div key={c.item} className="flex items-center gap-2 text-sm">
                  {c.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-slate-300" />}{c.item}
                </div>
              ))}
            </div>
          </div>
        )}
        {task.completion_notes && <Field label="Engineer Notes">{task.completion_notes}</Field>}
        <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{task.client?.address}</div>
      </DialogContent>
    </Dialog>
  );
};
